// Append-only, hash-chained audit log (PRD §6.5, §15.3).
//
// Each entry's `thisHash` is the SHA-256 hex digest of a canonical JSON encoding
// of {id, timestamp, eventType, skimReportId, payload, prevHash}. The chain
// starts at GENESIS_HASH (64 zeros). A verifier walks entries in insertion order
// and re-computes each hash, plus checks that `entry.prevHash` equals the prior
// entry's `thisHash` — any tampering breaks the chain.

import { getDb, type AuditLogRecord } from './db';

export const GENESIS_HASH = '0'.repeat(64);

export type AuditEventType =
  | 'skim_created'
  | 'analysis_completed'
  | 'decision_selected'
  | 'rationale_edited'
  | 'editor_notes_edited'
  | 'reviewer_selected'
  | 'reviewer_dismissed'
  | 'reviewer_added_manually'
  | 'finding_confirmed'
  | 'finding_dismissed'
  | 'finding_revisited'
  | 'exported'
  | 'settings_changed';

function canonicalJson(obj: Record<string, unknown>): string {
  // Stable key ordering for deterministic hashing.
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const k of keys) {
    parts.push(JSON.stringify(k) + ':' + canonicalValue(obj[k]));
  }
  return '{' + parts.join(',') + '}';
}

function canonicalValue(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return '[' + v.map(canonicalValue).join(',') + ']';
  if (typeof v === 'object') return canonicalJson(v as Record<string, unknown>);
  return JSON.stringify(v);
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const arr = new Uint8Array(digest);
  let out = '';
  for (const b of arr) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * The current chain TIP's thisHash (or GENESIS for an empty log).
 *
 * Must NOT use the `by_timestamp` index: timestamps are millisecond Date.now()
 * values, so a same-millisecond burst made the index cursor pick an arbitrary
 * same-ms record (tie-broken by random-UUID primary key). That produced a
 * *forked* chain (two records sharing one prevHash), which then read back
 * nondeterministically and could be misreported as tampered. Instead, the tip
 * is the record nobody links back to — i.e. its `thisHash` is not any
 * record's `prevHash`. A correct linear chain has exactly one such record.
 */
async function lastHash(): Promise<string> {
  const db = await getDb();
  const all = await db.getAll('audit_log');
  if (all.length === 0) return GENESIS_HASH;
  const linkedFrom = new Set(all.map((r) => r.prevHash));
  const tips = all.filter((r) => !linkedFrom.has(r.thisHash));
  if (tips.length === 1) return tips[0].thisHash;
  // Degenerate (corrupt/forked/empty): pick the newest by timestamp then id
  // so appends still resolve deterministically rather than randomly.
  return [...all].sort(
    (a, b) => b.timestamp - a.timestamp || (a.id < b.id ? 1 : -1),
  )[0].thisHash;
}

// Serialise appends. Some callers fire `void logAuditEvent(...)` without
// awaiting; two overlapping appends would both read the same tip and fork the
// chain. A promise queue makes every append atomic w.r.t. the others.
let appendQueue: Promise<unknown> = Promise.resolve();

export interface LogEventInput {
  eventType: AuditEventType;
  skimReportId: string | null;
  payload: Record<string, unknown>;
}

export function logAuditEvent(input: LogEventInput): Promise<AuditLogRecord> {
  const run = appendQueue.then(() => appendOne(input));
  // Keep the queue alive regardless of this append's outcome.
  appendQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

async function appendOne(input: LogEventInput): Promise<AuditLogRecord> {
  const id = crypto.randomUUID();
  const timestamp = Date.now();
  const prevHash = await lastHash();
  const toHash = canonicalJson({
    id,
    timestamp,
    eventType: input.eventType,
    skimReportId: input.skimReportId,
    payload: input.payload,
    prevHash,
  });
  const thisHash = await sha256Hex(toHash);
  const record: AuditLogRecord = {
    id,
    timestamp,
    eventType: input.eventType,
    skimReportId: input.skimReportId,
    payload: input.payload,
    prevHash,
    thisHash,
  };
  const db = await getDb();
  await db.add('audit_log', record);
  return record;
}

/**
 * Returns the log in true append-only order by walking the hash chain
 * (GENESIS → each record whose prevHash points at the previous thisHash).
 *
 * Storage/index order is NOT reliable: `timestamp` is `Date.now()`
 * (millisecond resolution), so entries written in the same millisecond share
 * the `by_timestamp` index key and IndexedDB then orders them by the object
 * store's primary key — which is a random `crypto.randomUUID()`. That made
 * `readAuditLog` (and therefore `verifyAuditLog`) nondeterministic: an
 * intact, untampered chain could be returned out of order and falsely
 * reported as tampered ("prevHash mismatch"). The log already IS a hash
 * linked-list, so we read it as one. No schema/index migration needed.
 *
 * If the chain cannot be fully reconstructed (genuine tampering, a fork from
 * a duplicated prevHash, or a cycle) we fall back to timestamp order so
 * `verifyAuditLog` still runs over every record and reports the real break
 * rather than silently truncating the log.
 */
export async function readAuditLog(): Promise<AuditLogRecord[]> {
  const db = await getDb();
  const all = await db.getAll('audit_log');
  if (all.length <= 1) return all;

  const byPrev = new Map<string, AuditLogRecord>();
  for (const r of all) byPrev.set(r.prevHash, r);

  const ordered: AuditLogRecord[] = [];
  const seen = new Set<string>();
  let cursor = GENESIS_HASH;
  for (;;) {
    const next = byPrev.get(cursor);
    if (!next || seen.has(next.id)) break; // end, broken, fork, or cycle
    ordered.push(next);
    seen.add(next.id);
    cursor = next.thisHash;
  }

  if (ordered.length !== all.length) {
    // Chain not fully reconstructable — preserve the original behaviour so
    // verifyAuditLog still inspects every record and flags the real problem.
    return [...all].sort((a, b) => a.timestamp - b.timestamp);
  }
  return ordered;
}

export interface VerifyResult {
  ok: boolean;
  count: number;
  brokenAt?: { id: string; reason: string };
}

export async function verifyAuditLog(): Promise<VerifyResult> {
  const entries = await readAuditLog();
  let prev = GENESIS_HASH;
  for (const e of entries) {
    if (e.prevHash !== prev) {
      return { ok: false, count: entries.length, brokenAt: { id: e.id, reason: 'prevHash mismatch' } };
    }
    const recomputed = await sha256Hex(
      canonicalJson({
        id: e.id,
        timestamp: e.timestamp,
        eventType: e.eventType,
        skimReportId: e.skimReportId,
        payload: e.payload,
        prevHash: e.prevHash,
      }),
    );
    if (recomputed !== e.thisHash) {
      return { ok: false, count: entries.length, brokenAt: { id: e.id, reason: 'thisHash mismatch' } };
    }
    prev = e.thisHash;
  }
  return { ok: true, count: entries.length };
}
